/* global __CARIMBO_BUILD__ */ // injetado pelo Vite (vite.config.js → define)
import { sessoesAtivas, terminarTodasAsSessoes, medirJanela } from './agoraSession';
import { resetStadiumUpdate, shouldShowStadiumUpdate, dataLocalAAAAMMDD } from './stadiumUpdate';

// DIAGNÓSTICO DE SESSÕES — para a verificação no telemóvel/navegador.
//
// A matriz automática (npm run test:matriz) prova a lógica, mas não abre um
// navegador nem fala com os servidores do Agora. Isto é o instrumento para a
// outra metade: depois de sair de um estádio, abre a consola e escreve
//
//   gfl.limpo()
//
// e ficas a saber, com números em vez de palpites, se sobrou alguma câmara
// acesa ou algum listener por desligar.
//
// LIMITE HONESTO: só conta o que passa pelo gestor de sessão. Os onSnapshot
// que cada useEffect abre e fecha por sua conta não aparecem aqui — para
// esses, o VERIFICACAO.md explica como usar o painel de memória do navegador.

function elementosDeVideoVivos() {
  if (typeof document === 'undefined') return [];
  return [...document.querySelectorAll('video')]
    .filter((v) => v.srcObject && v.srcObject.getTracks?.().some((t) => t.readyState === 'live'))
    .map((v) => ({
      id: v.id || v.parentElement?.id || '(sem id)',
      pistas: v.srcObject.getTracks().filter((t) => t.readyState === 'live').map((t) => t.kind),
    }));
}

export function diagnostico() {
  const sessoes = sessoesAtivas();
  const videos = elementosDeVideoVivos();
  return {
    sessoesVivas: sessoes.length,
    sessoes,
    pistasNasSessoes: sessoes.reduce((t, s) => t + s.pistasVivas, 0),
    listenersRegistados: sessoes.reduce((t, s) => t + s.listenersFirebase, 0),
    videosVivosNoEcra: videos.length,
    videos,
  };
}

// A pergunta do fim do plano: ficou alguma coisa por desligar?
// Devolve { limpo, problemas[] } e imprime um resumo legível.
export function limpo() {
  const d = diagnostico();
  const problemas = [];

  if (d.sessoesVivas > 0) {
    problemas.push(`${d.sessoesVivas} sessão(ões) Agora ainda viva(s): `
      + d.sessoes.map((s) => `${s.etiqueta}(${s.estadoLigacao})`).join(', '));
  }
  if (d.pistasNasSessoes > 0) problemas.push(`${d.pistasNasSessoes} câmara(s)/microfone(s) por fechar`);
  if (d.listenersRegistados > 0) problemas.push(`${d.listenersRegistados} listener(s) do Firebase por desligar`);
  if (d.videosVivosNoEcra > 0) {
    problemas.push(`${d.videosVivosNoEcra} elemento(s) <video> ainda a receber imagem: `
      + d.videos.map((v) => v.id).join(', '));
  }

  const estaLimpo = problemas.length === 0;
  if (estaLimpo) {
    console.log('%c✓ LIMPO — zero sessões, zero câmaras, zero listeners.',
      'color:#39FF14;font-weight:bold');
  } else {
    console.warn('%c✗ SOBROU COISA:', 'color:#ff4444;font-weight:bold');
    problemas.forEach((p) => console.warn('   • ' + p));
    console.table(d.sessoes);
  }
  return { limpo: estaLimpo, problemas, detalhe: d };
}

// Faz o aviso diário voltar a aparecer no próximo ecrã de jogo. Serve para os
// testes manuais não terem de limpar o localStorage à mão de cada vez.
export function reporAviso() {
  const conseguiu = resetStadiumUpdate();
  console.log(conseguiu
    ? '%c✓ Aviso reposto — vai aparecer da próxima vez que entrares num estádio.'
    : '%c✗ Não foi possível limpar (localStorage bloqueado?).',
    `color:${conseguiu ? '#39FF14' : '#ff4444'};font-weight:bold`);
  return conseguiu;
}

// Estado do aviso diário: já foi visto hoje?
export function estadoDoAviso() {
  return { hoje: dataLocalAAAAMMDD(), vaiAparecer: shouldShowStadiumUpdate() };
}

// O que está mesmo em cada janela de vídeo: tamanho da janela, tamanho do
// <video>, resolução da pista e se enche. Serve para distinguir "o vídeo não
// enche a janela" (problema nosso) de "a imagem que chega é assim" (câmara).
export function janelas() {
  if (typeof document === 'undefined') return [];
  const lista = [...document.querySelectorAll('.janela-video')].map((janela) => {
    const video = janela.querySelector('video');
    const m = video ? medirJanela(janela, video) : null;
    const caixa = janela.getBoundingClientRect();
    const cs = video && typeof getComputedStyle === 'function' ? getComputedStyle(video) : null;
    return {
      id: janela.id || '(sem id)',
      filhos: [...janela.children]
        .map((f) => f.tagName.toLowerCase() + (f.id ? '#' + f.id : '') + (f.className ? '.' + String(f.className).split(' ')[0] : ''))
        .join(', '),
      videos: janela.querySelectorAll('video').length,
      janela: `${Math.round(caixa.width)}×${Math.round(caixa.height)}`,
      video: m ? m.video : '(sem vídeo)',
      pista: m ? m.pista : '-',
      enche: m ? m.enche : false,
      estilo: cs ? `${cs.position} ${cs.width}×${cs.height} ${cs.objectFit}` : '-',
      aTocar: !!video && !video.paused && video.readyState >= 2,
    };
  });
  console.table(lista);
  return lista;
}

// Que versão está a correr (data/hora do build, injetada pelo Vite).
export function versao() {
  const carimbo = typeof __CARIMBO_BUILD__ !== 'undefined' ? __CARIMBO_BUILD__ : 'dev';
  console.log('%cBuild: ' + carimbo, 'color:#39FF14');
  return carimbo;
}

// Quantos trabalhadores (service workers) estão registados, e quais. É o
// instrumento para confirmar no telemóvel que a caixa "Novidades no Estádio!"
// não volta: tem de aparecer UM só, o /sw.js. Dois ou mais em "/" e eles
// expulsam-se um ao outro a cada carregamento — era esse o bug.
export async function trabalhadores() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    console.warn('Este navegador não tem service workers.');
    return { suportado: false, registados: [] };
  }
  const registos = await navigator.serviceWorker.getRegistrations();
  const lista = registos.map((r) => ({
    ficheiro: (r.active || r.waiting || r.installing)?.scriptURL || '(nenhum)',
    estado: r.active ? 'ativo' : (r.waiting ? 'à espera' : 'a instalar'),
    ambito: r.scope,
  }));
  const aControlar = navigator.serviceWorker.controller?.scriptURL || '(nenhum)';
  console.table(lista);
  console.log('A controlar esta página: ' + aControlar);
  if (lista.length === 1) {
    console.log('%c✓ UM SÓ TRABALHADOR', 'color:#39FF14;font-weight:bold');
  } else {
    console.warn(`✗ ${lista.length} TRABALHADORES — deviam ser 1`);
  }
  return { suportado: true, registados: lista, aControlar, ok: lista.length === 1 };
}

// Escotilha de emergência: fecha tudo à força.
export async function fecharTudo() {
  await terminarTodasAsSessoes();
  return limpo();
}


// ===========================================================================
// VIGIA DO SALDO — prova, com números, que ninguém sobrepõe o saldo
// ===========================================================================
//
// O cenário a verificar é o da auditoria: pôr `saldoBolas` à mão na consola do
// Firebase e confirmar que (a) o valor chega ao ecrã sem recarregar a página, e
// (b) nenhuma navegação o volta a escrever.
//
//   gfl.saldo()          → começa a vigiar e imprime cada alteração
//   gfl.saldo.parar()    → desliga a vigia
//
// Cada linha diz de onde veio a alteração: `servidor` é uma escrita confirmada,
// `local` é uma escrita ainda por confirmar feita por este próprio separador.
// Se aparecer uma descida para 0 que tu não provocaste, é aí que está o bug.
let pararVigiaDoSaldo = null;

export async function saldo() {
  const { auth, db } = await import('./firebase');
  const { doc, onSnapshot } = await import('firebase/firestore');

  const uid = auth.currentUser?.uid;
  if (!uid) {
    console.warn('Ninguém autenticado neste separador.');
    return null;
  }

  if (pararVigiaDoSaldo) pararVigiaDoSaldo();

  let anterior = null;
  console.log(`%cA vigiar utilizadores/${uid}`, 'color:#FFD700;font-weight:bold');

  pararVigiaDoSaldo = onSnapshot(doc(db, 'utilizadores', uid), (snap) => {
    if (!snap.exists()) { console.warn('A ficha deste utilizador NÃO existe.'); return; }

    const d = snap.data();
    const bolas = Number(d.saldoBolas);
    const origem = snap.metadata.hasPendingWrites ? 'local' : 'servidor';
    const hora = new Date().toLocaleTimeString();

            if (anterior === null) {
          console.log(`%c[${hora}] inicial • ${bolas.toLocaleString()} Bolas ⚽ (${origem})`, 'color:#39FF14');
        } else {
          const delta = bolas - anterior;
          const sinal = delta > 0 ? '+' : '';
          // Uma queda a pique para zero é a assinatura do bug que se corrigiu:
          // alguém a gravar um total absoluto por cima do valor real.
          const suspeito = bolas === 0 && anterior > 0;
          console.log(
            `%c[${hora}] ${anterior.toLocaleString()} -> ${bolas.toLocaleString()} Bolas ⚽ (${sinal}${delta.toLocaleString()}) (${origem})` + 
            (suspeito ? ' ⚠️ ZERADO — isto não devia acontecer' : ''),
            suspeito ? 'color:#FF3131;font-weight:bold' : 'color:#39FF14'
          );
        }
    anterior = bolas;
  }, (erro) => console.error('A vigia do saldo parou:', erro));

  return uid;
}

saldo.parar = () => {
  if (pararVigiaDoSaldo) { pararVigiaDoSaldo(); pararVigiaDoSaldo = null; console.log('Vigia do saldo desligada.'); }
  else console.log('Não havia vigia ligada.');
};

// ===========================================================================
// VIGIA DOS PRESENTES — mede quanto tempo cada presente demora a chegar
// ===========================================================================
//
//   gfl.presentes()            → vigia a bancada do próprio utilizador
//   gfl.presentes('<uid>')     → vigia a bancada de outro anfitrião
//   gfl.presentes.parar()
//
// O id da bancada é o uid do anfitrião. Para cada anúncio imprime o atraso
// entre o carimbo do servidor e o momento em que chegou a este ecrã — é este
// número que responde a "o presente aparece no momento certo para todos?".
let pararVigiaDePresentes = null;

export async function presentes(idDaSala) {
  const { auth, db } = await import('./firebase');
  const { collection, onSnapshot } = await import('firebase/firestore');

  const sala = idDaSala || auth.currentUser?.uid;
  if (!sala) { console.warn('Sem id de bancada e ninguém autenticado.'); return null; }

  if (pararVigiaDePresentes) pararVigiaDePresentes();

  console.log(`%cA vigiar os presentes da bancada ${sala}`, 'color:#FFD700;font-weight:bold');
  let contados = 0;

  pararVigiaDePresentes = onSnapshot(
    collection(db, 'salas_bancada', sala, 'animacoes_presentes'),
    (snap) => {
      snap.docChanges().forEach((m) => {
        if (m.type !== 'added') return;
        const d = m.doc.data() || {};
        const carimbo = d.criadoEm?.toMillis?.() ?? null;
        const atraso = carimbo === null ? 'local (ainda por confirmar)' : `${Date.now() - carimbo} ms`;
        contados += 1;
        console.log(
          `%c#${contados} ${d.presenteNome || '?'} · de ${d.deNome || '?'} (${d.deUid || 'SEM deUid'})`
          + ` → ${d.paraNome || '?'} · ${d.videoUrl ? 'MP4' : 'imagem'}${d.isLucky ? ' · LUCKY' : ''}`
          + ` · atraso ${atraso}`,
          d.deUid ? 'color:#39FF14' : 'color:#FF3131;font-weight:bold',
        );
      });
      const emFila = snap.size;
      // A subcolecção tem de se manter curta: cada anúncio apaga-se aos 25 s.
      // Se este número crescer sem parar, as auto-limpezas não estão a correr.
      if (emFila > 12) console.warn(`Há ${emFila} anúncios por apagar na bancada — devia baixar sozinho.`);
    },
    (erro) => console.error('A vigia dos presentes parou:', erro),
  );

  return sala;
}

presentes.parar = () => {
  if (pararVigiaDePresentes) { pararVigiaDePresentes(); pararVigiaDePresentes = null; console.log('Vigia dos presentes desligada.'); }
  else console.log('Não havia vigia ligada.');
};

// Publica em window.gfl. Não altera comportamento nenhum — só lê e reporta.
export function instalarDiagnostico() {
  if (typeof window === 'undefined') return;
  window.gfl = { ...(window.gfl || {}), diagnostico, limpo, fecharTudo, reporAviso, estadoDoAviso, janelas, versao, trabalhadores, saldo, presentes };
  versao();
  console.log('%cDiagnóstico pronto: gfl.limpo() · gfl.diagnostico() · gfl.janelas() · gfl.trabalhadores() · gfl.reporAviso() · gfl.fecharTudo()',
    'color:#39FF14');
  console.log('%cVerificação desta correção: gfl.saldo() · gfl.presentes()', 'color:#FFD700');
}
